<script setup>
/* eslint-disable @intlify/vue-i18n/no-dynamic-keys -- call states use the fixed catalog. */
import { computed, onMounted, ref } from 'vue';
import { useToggle } from '@vueuse/core';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import {
  getVoiceCallProvider,
  VOICE_CALL_PROVIDERS,
} from 'dashboard/helper/inbox';
import {
  VOICE_CALL_DIRECTION,
  VOICE_CALL_OUTBOUND_INIT_STATUS,
} from 'dashboard/components-next/message/constants';
import { useWhatsappCallSession } from 'dashboard/composables/useWhatsappCallSession';
import { useCallsStore } from 'dashboard/stores/calls';
import { useMapGetter } from 'dashboard/composables/store';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
  chat: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();
const store = useStore();
const callsStore = useCallsStore();
const whatsappCallSession = useWhatsappCallSession();
const currentUser = useMapGetter('getCurrentUser');
const { isCloudFeatureEnabled } = useAccount();
const threeCxIntegration = ref(null);
const isInitiatingThreeCxCall = ref(false);
const [showCallMethods, toggleCallMethods] = useToggle(false);

const voiceCallProvider = computed(() => getVoiceCallProvider(props.inbox));
const isVoiceCallInbox = computed(
  () =>
    voiceCallProvider.value !== null &&
    isCloudFeatureEnabled(FEATURE_FLAGS.CHANNEL_VOICE)
);
const isWhatsappVoiceInbox = computed(
  () => voiceCallProvider.value === VOICE_CALL_PROVIDERS.WHATSAPP
);
const currentContact = computed(() =>
  store.getters['contacts/getContact'](props.chat?.meta?.sender?.id)
);

const threeCxExtension = computed(() => {
  const customAttributes = currentUser.value?.custom_attributes || {};
  return (
    customAttributes.threecx_dn ||
    customAttributes.threecx_extension ||
    customAttributes.ramal ||
    customAttributes.extension ||
    ''
  );
});

const contactPhone = computed(
  () =>
    currentContact.value?.phone_number ||
    props.chat?.meta?.sender?.phone_number ||
    props.chat?.meta?.sender?.phoneNumber ||
    ''
);

const isThreeCxCallAvailable = computed(() =>
  Boolean(
    threeCxIntegration.value?.available &&
      threeCxIntegration.value?.configured &&
      threeCxIntegration.value?.settings?.threecx_integration_enabled &&
      threeCxExtension.value &&
      contactPhone.value
  )
);
const canChooseCallMethod = computed(
  () => isWhatsappVoiceInbox.value && isThreeCxCallAvailable.value
);

const isCallButtonDisabled = computed(
  () =>
    callsStore.hasActiveCall ||
    callsStore.hasIncomingCall ||
    (isThreeCxCallAvailable.value && isInitiatingThreeCxCall.value) ||
    (isWhatsappVoiceInbox.value && whatsappCallSession.isInitiating.value)
);

const isCallButtonLoading = computed(() => {
  if (canChooseCallMethod.value) {
    return (
      isInitiatingThreeCxCall.value || whatsappCallSession.isInitiating.value
    );
  }
  if (isThreeCxCallAvailable.value) return isInitiatingThreeCxCall.value;
  return whatsappCallSession.isInitiating.value;
});

const callButtonTooltip = computed(() => {
  if (canChooseCallMethod.value) {
    return t('CONVERSATION.HEADER.CHOOSE_CALL_METHOD');
  }
  if (isThreeCxCallAvailable.value) {
    return t('CONVERSATION.HEADER.THREE_CX_CALL', {
      extension: threeCxExtension.value,
    });
  }
  return isWhatsappVoiceInbox.value
    ? t('CONVERSATION.HEADER.WHATSAPP_CALL')
    : t('CONVERSATION.HEADER.VOICE_CALL');
});

const callMethodMenuItems = computed(() => [
  {
    label: t('CONVERSATION.HEADER.CALL_VIA_WHATSAPP'),
    value: 'whatsapp',
    action: 'call',
    icon: 'i-lucide-phone',
  },
  {
    label: t('CONVERSATION.HEADER.CALL_VIA_THREE_CX'),
    value: 'three_cx',
    action: 'call',
    icon: 'i-lucide-headset',
  },
]);

const loadThreeCxStatus = async () => {
  try {
    const accountId = store.getters.getCurrentAccountId;
    const { data } = await window.axios.get(
      `/api/v1/accounts/${accountId}/native_integrations`
    );
    threeCxIntegration.value = data.find(
      integration => integration.key === 'three_cx'
    );
  } catch (_) {
    threeCxIntegration.value = null;
  }
};

const startWhatsappCall = async () => {
  if (whatsappCallSession.isInitiating.value) return;
  try {
    const response = await whatsappCallSession.initiateOutboundCall(
      props.chat.id
    );

    // Composable returns LOCKED when init is already in flight or a call is
    // active; soft no-op so a parallel click doesn't trigger a banner.
    if (response?.status === VOICE_CALL_OUTBOUND_INIT_STATUS.LOCKED) return;
    // Permission template path returns no call id — show banner, no widget yet.
    if (!response?.id) {
      const status = response?.status;
      const messageKey =
        status === VOICE_CALL_OUTBOUND_INIT_STATUS.PERMISSION_PENDING
          ? 'CONVERSATION.HEADER.WHATSAPP_CALL_PERMISSION_PENDING'
          : 'CONVERSATION.HEADER.WHATSAPP_CALL_PERMISSION_REQUESTED';
      useAlert(t(messageKey));
      return;
    }

    // Stay non-active until Meta delivers the connect webhook (sdp_answer);
    // flipping to active here would start the duration timer before pickup.
    callsStore.addCall({
      callSid: response.call_id,
      callId: response.id,
      conversationId: props.chat.id,
      inboxId: props.inbox?.id,
      callDirection: VOICE_CALL_DIRECTION.OUTBOUND,
      provider: VOICE_CALL_PROVIDERS.WHATSAPP,
    });
  } catch (error) {
    useAlert(error?.message || t('CONVERSATION.HEADER.WHATSAPP_CALL_FAILED'));
  }
};

const startThreeCxCall = async () => {
  if (isInitiatingThreeCxCall.value) return;

  isInitiatingThreeCxCall.value = true;
  try {
    const accountId = store.getters.getCurrentAccountId;
    const contactId = props.chat?.meta?.sender?.id;
    await window.axios.post(
      `/api/v1/accounts/${accountId}/contacts/${contactId}/three_cx_call`
    );
    useAlert(
      t('CONVERSATION.HEADER.THREE_CX_CALL_STARTED', {
        extension: threeCxExtension.value,
      })
    );
  } catch (error) {
    useAlert(
      error.response?.data?.error ||
        t('CONVERSATION.HEADER.THREE_CX_CALL_FAILED')
    );
  } finally {
    isInitiatingThreeCxCall.value = false;
  }
};

const startCall = () => {
  if (canChooseCallMethod.value) {
    toggleCallMethods();
    return;
  }
  if (isThreeCxCallAvailable.value) {
    startThreeCxCall();
    return;
  }
  if (isWhatsappVoiceInbox.value) startWhatsappCall();
};

const handleCallMethodAction = ({ value }) => {
  toggleCallMethods(false);
  if (value === 'three_cx') {
    startThreeCxCall();
    return;
  }
  if (value === 'whatsapp') startWhatsappCall();
};

onMounted(loadThreeCxStatus);
</script>

<template>
  <div
    v-if="isVoiceCallInbox || isThreeCxCallAvailable"
    v-on-clickaway="() => toggleCallMethods(false)"
    class="relative"
  >
    <NextButton
      v-tooltip.bottom="callButtonTooltip"
      sm
      ghost
      slate
      icon="i-lucide-phone"
      :is-loading="isCallButtonLoading"
      :disabled="isCallButtonDisabled"
      :aria-label="callButtonTooltip"
      @click="startCall"
    />
    <DropdownMenu
      v-if="canChooseCallMethod && showCallMethods"
      :menu-items="callMethodMenuItems"
      class="mt-1 ltr:right-0 rtl:left-0 top-full"
      @action="handleCallMethodAction"
    />
  </div>
</template>
